// Admin mirror of the web app's src/Organizer/Oireachtas/OrganizerStatus.jsx (the organiser panel
// copy, Devin 2026-09-26) — keep them in step.
// The organiser's read-only Status tab (Devin 2026-11-02, card f4270e09):
// where each of today's competitions is through its tabulation — rounds,
// recall, round 3, results — refreshed every 10 seconds. Nothing to press:
// the admin panel runs the event ("they can't publish, they can only see the
// status, read only"). Same cards as the admin Status screen, organiser
// wording, no judge or tabulator names (the server leaves them out).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Msr } from './symbols';
import { getOrganizerStatus } from './api';
import { getSchedule } from './Schedule/scheduleApi';
import useAutoRefresh from '../ManageOireachtas/useAutoRefresh';
import {
  ALL,
  chipsOf,
  inChip,
  sortRows,
  todayRows,
  stepsOf,
  headlineOf,
  summaryOf,
  scheduledToday,
  eventDayKey,
} from './organizerStatusModel';
import './organizerStatus.css';

const REFRESH_MS = 10 * 1000;
// the stage schedule is read again this often, so a schedule change shows
// (code review 62af0313 R1-F1); "today" is worked out again on every refresh
const SCHEDULE_MS = 5 * 60 * 1000;
const timeLabel = (d) => (d ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '');
const STEP_ICON = { done: 'check', current: 'more_horiz', waiting: '' };

// one competition (module level: never define components in render)
function StatusCard({ row }) {
  const c = row.competition;
  const head = headlineOf(row);
  return (
    <section className={`card os-card ${head.tone}`} aria-label={`${c.code} ${c.name}`}>
      <div className="os-head">
        <div className="os-title">
          <b className="os-code">{c.code}</b>
          <span className="os-name">{c.name}</span>
        </div>
        <span className={`op-pill os-pill ${head.tone}`}>{head.short}</span>
      </div>
      <ol className="os-track">
        {stepsOf(row).map((s) => (
          <li key={s.key} className={`os-step ${s.state}${s.alert ? ' attn' : ''}`}>
            <span className="os-dot" aria-hidden="true">
              {STEP_ICON[s.state] ? <Msr>{STEP_ICON[s.state]}</Msr> : null}
            </span>
            <span className="os-label">{s.label}</span>
            {s.detail && <span className="os-detail">{s.detail}</span>}
          </li>
        ))}
      </ol>
      <div className="os-line">{head.line}</div>
    </section>
  );
}

export default function OrganizerStatus({ eventId }) {
  const [rows, setRows] = useState(null);
  const [loadError, setLoadError] = useState('');
  // the stage schedule and the event's time zone; today's competitions are
  // worked out from them on every render (a tab left open overnight moves to
  // the next day — code review 62af0313 R1-F1)
  const [schedule, setSchedule] = useState(null);
  const [timeZone, setTimeZone] = useState('');
  const scheduleAt = useRef(0);
  const [showAll, setShowAll] = useState(false);
  const [chip, setChip] = useState(ALL);
  const [loadedAt, setLoadedAt] = useState(null);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const seq = useRef(0); // only the newest load is shown

  // a failed re-read keeps the schedule already shown; no schedule just means
  // every competition is listed
  const readSchedule = async () => {
    scheduleAt.current = Date.now();
    const res = await getSchedule(eventId).catch(() => undefined);
    if (res !== undefined) setSchedule(res);
  };

  const load = async (background) => {
    const mine = ++seq.current;
    try {
      const res = await getOrganizerStatus(eventId);
      if (mine !== seq.current) return;
      if (res.event?.timeZone) setTimeZone(res.event.timeZone);
      setRows(res.rows || []);
      setLoadedAt(new Date());
      setRefreshFailed(false);
      setLoadError('');
      return res;
    } catch (e) {
      if (mine !== seq.current) return;
      if (background) setRefreshFailed(true);
      else setLoadError(e?.messages || 'Could not load the status');
      throw e;
    }
  };

  useEffect(() => {
    load(false).catch(() => {});
    readSchedule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  useAutoRefresh(
    () => {
      if (Date.now() - scheduleAt.current > SCHEDULE_MS) readSchedule();
      return load(true);
    },
    { everyMs: REFRESH_MS, active: rows !== null }
  );

  const sorted = useMemo(() => sortRows(rows || []), [rows]);
  if (loadError && rows === null) {
    return (
      <div className="op-error" role="alert">
        {loadError}{' '}
        <button type="button" className="op-btn sm" onClick={() => load(false).catch(() => {})}>
          <Msr>refresh</Msr>Try again
        </button>
      </div>
    );
  }
  if (rows === null) return <div className="op-empty">Loading…</div>;

  const todayIds = scheduledToday(schedule?.schedule, eventDayKey(schedule?.event?.timeZone || timeZone));
  const dayRows = todayRows(sorted, todayIds, showAll);
  const chips = chipsOf(dayRows);
  const chipOn = chips.includes(chip) ? chip : ALL;
  const shown = dayRows.filter((r) => inChip(r, chipOn));
  const summary = summaryOf(shown);

  return (
    <div className="os-page">
      <div className={`os-updated${refreshFailed ? ' failed' : ''}`} role="status">
        {refreshFailed
          ? `Couldn't refresh — showing ${timeLabel(loadedAt)}`
          : `Updated ${timeLabel(loadedAt)} · refreshes every 10 seconds`}
      </div>
      <div className="os-filters">
        {todayIds && (
          <div className="os-day">
            <b>{showAll ? 'Every competition' : "Today's competitions"}</b>
            <button type="button" className="os-showall" onClick={() => setShowAll(!showAll)}>
              {showAll ? "Only today's competitions" : 'Show every competition'}
            </button>
          </div>
        )}
        {chips.length > 2 && (
          <div className="os-chips" role="group" aria-label="Competition type">
            {chips.map((ch) => (
              <button
                key={ch}
                type="button"
                className={`op-chip${ch === chipOn ? ' on' : ''}`}
                aria-pressed={ch === chipOn}
                onClick={() => setChip(ch)}
              >
                {ch}
              </button>
            ))}
          </div>
        )}
        <div className="os-summary">
          {summary.total} competition{summary.total === 1 ? '' : 's'}
          {summary.inProgress ? ` · ${summary.inProgress} in progress` : ''}
          {summary.signOff ? ` · ${summary.signOff} awaiting sign-off` : ''}
          {summary.ready ? ` · ${summary.ready} ready to publish` : ''}
          {summary.published ? ` · ${summary.published} published` : ''}
          {summary.notDrawn ? ` · ${summary.notDrawn} not drawn` : ''}
        </div>
      </div>
      <div className="os-list">
        {shown.map((row) => (
          <StatusCard key={row.competition._id} row={row} />
        ))}
      </div>
      {shown.length === 0 && <div className="op-empty">No competitions to show.</div>}
    </div>
  );
}
